import { StateCreator } from "zustand";
import { supabase } from "@/lib/supabase";
import {
	SupabaseTableEnum,
	AkontoViewType,
	VerlustViewType,
	OpenInvoicesViewType,
} from "@/lib/supabase/supabaseTypes";
import { TableInvoiceRow } from "../../pages/debitoren/types/invoice-table.types";
import { PaymentJournalRow } from "../../pages/debitoren/types/payment-journal.types";
import { Logger } from "@/lib/logger/Logger";
import { useCentralStore } from "../Central";
import { usePatientStore } from "../Patient";
import { useRemindersStore } from "../Reminders";
import { RecipientTypeEnum } from "../../pages/monthly-invoices/types";
import { getClientDisplayName } from "@/lib/utils/client-utils";
import {
	BankAccountEntityType,
	ClientEntityType,
	JobDocumentEntityType,
	JobEntityType,
	OrganizationEntityType,
	SupabaseRpcEnum,
} from "@/lib/supabase/supabaseTypes";
import { PdfTemplateType } from "../../pdf-templates/document-template.types";
import { InvoiceType } from "../../types/database-enums/invoices-enums";
import { PaymentType } from "../../types/database-enums/payment-enums";
import { showNotification } from "../Central/selectors";
import { getGuarantorDisplayName } from "../../lib/utils/guarantor-utils";
import { ExtendedJobDocument } from "../../lib/utils/calculate";
import { produce } from "immer";
import { formatLocalDate } from "@/lib/utils/format-local-date";
import { sortTableData } from "../../pages/debitoren/utils/sorting-utils";
import { getCurrencyFromRegion } from "../../lib/utils/region-utils";
import { SUPABASE_PAGE_SIZE } from "../../lib/utils/utils-functions";
import type { Database } from "@/lib/supabase/supabaseTypesImport";

type RecipientsLookupValue = { type: RecipientTypeEnum; name: string };

type OpenInvoiceAtDateRow =
	Database["public"]["Functions"]["get_open_invoices_at_date"]["Returns"][number];

const fetchRpcPage = async <Row extends { total_count: number }>(
	pageIndex: number,
	pageSize: number,
	fetchRows: (
		limit: number,
		offset: number
	) => PromiseLike<{ data: Row[] | null; error: { message: string } | null }>
): Promise<{ rows: Row[]; totalCount: number; error?: string }> => {
	const rows: Row[] = [];
	let totalCount = 0;
	while (rows.length < pageSize) {
		const limit = Math.min(SUPABASE_PAGE_SIZE, pageSize - rows.length);
		const { data, error } = await fetchRows(
			limit,
			pageIndex * pageSize + rows.length
		);
		if (error) return { rows: [], totalCount: 0, error: error.message };
		const chunk = data ?? [];
		if (chunk.length === 0) break;
		if (rows.length > 0 && Number(chunk[0].total_count) !== totalCount) {
			return {
				rows: [],
				totalCount: 0,
				error: "Rows changed while loading",
			};
		}
		totalCount = Number(chunk[0].total_count);
		rows.push(...chunk);
		if (chunk.length < limit) break;
	}
	return { rows, totalCount };
};

interface CreateInvoiceSingleProps {
	fileNamePrefix: string;
	invoiceValue: number;
	bankAccount: BankAccountEntityType;
	invoiceType: InvoiceType;
	pdfTemplateType: PdfTemplateType;
	client: ClientEntityType;
	organization: OrganizationEntityType;
	jobDocument: JobDocumentEntityType;
	job: JobEntityType;
	akontoAmount?: number;
}

interface CreateInvoiceMonthlyProps {
	invoiceValue: number;
	bankAccountId: number;
	clientId: string | null;
	guarantorId: string | null;
	organizationId: string;
	jobDocumentIds: number[];
	invoiceDate: Date;
	akontoAmount?: number;
}

interface CreateInvoiceReturnType {
	success: boolean;
	data: {
		invoiceNumber: string;
		filePath: string;
		akontoUsed: number;
	} | null;
	error: string | null;
}

const isSrg = (row: TableInvoiceRow | OpenInvoicesViewType) => {
	return (
		(row.meta_data as { type: string; pdf_template_type: string })
			?.pdf_template_type === PdfTemplateType.SAMMELRECHNUNG
	);
};

export interface CreatePaymentProps {
	invoiceId: number;
	amount: number;
	valutaDate: string;
	paymentType: PaymentType;
	note: string;
	generatePaymentSlip: boolean;
	bookRemainderAsLoss: boolean;
	lossComment: string;
}

interface CreatePaymentReturnType {
	success: boolean;
	data: {
		paymentId: number;
		allocationId: number;
		akontoAllocationId?: number;
		lossAllocationId?: number;
		invoiceFullyPaid: boolean;
		invoicePaymentAmount: number;
		akontoAmount?: number;
		akontoInvoiceId?: number;
		lossAmount?: number;
	} | null;
	error: string | null;
}

export interface AccountingSlice {
	invoicePaymentsData: TableInvoiceRow[];
	paymentJournalData: PaymentJournalRow[];
	openInvoicesData: OpenInvoicesViewType[];
	akontoData: AkontoViewType[];
	verlustData: VerlustViewType[];
	canceledInvoicesData: TableInvoiceRow[];
	recipientsLookup: Record<string, RecipientsLookupValue>;
	akontoLookup: Record<string, number>;
	fetchRecipients: () => Promise<void>;
	fetchAkontoView: () => Promise<void>;
	getAkonto: (
		recipientClientId: string | null,
		recipientGuarantorId: string | null,
		recipientPatientId: string | null
	) => Promise<number>;
	clearAkontoLookup: () => void;
	fetchInvoicePaymentsData: (
		startDate: string,
		endDate: string,
		recipient: string,
		options: {
			status?: "active" | "canceled" | "all";
			sortBy?: string;
			sortDirection?: "asc" | "desc";
			pageIndex?: number;
			pageSize?: number;
			globalFilter?: string;
			columnFilters?: { id: string; value: unknown }[];
			updateStore: boolean;
		}
	) => Promise<{
		data: TableInvoiceRow[];
		totalCount: number;
		error?: string;
	}>;
	fetchPaymentJournalData: (
		startDate: string,
		endDate: string,
		recipient: string,
		options: {
			sortBy?: string;
			sortDirection?: "asc" | "desc";
			pageIndex?: number;
			pageSize?: number;
			globalFilter?: string;
			updateStore: boolean;
		}
	) => Promise<{
		data: PaymentJournalRow[];
		totalCount: number;
		error?: string;
	}>;
	markInvoiceAsPaid: (invoiceId: number) => Promise<void>;
	createPayment: (
		props: CreatePaymentProps
	) => Promise<CreatePaymentReturnType>;
	cancelPayment: (
		allocationId: number,
		invoiceId: number,
		note: string
	) => Promise<void>;
	cancelLossAllocation: (
		allocationId: number,
		invoiceId: number,
		note: string
	) => Promise<void>;
	rebookPaymentAsAkonto: (
		allocationId: number,
		invoiceId: number,
		note: string
	) => Promise<void>;
	cancelInvoice: (
		invoiceId: number,
		reason: string,
		rebookAsAkonto?: boolean,
		cancelReminderFees?: boolean
	) => Promise<void>;
	refreshSingleAccountingData: (invoiceId: number) => Promise<void>;
	refreshSingleInvoicePaymentsData: (invoiceId: number) => Promise<void>;
	createInvoiceSingle: (
		props: CreateInvoiceSingleProps
	) => Promise<CreateInvoiceReturnType>;
	createInvoiceMonthly: (
		props: CreateInvoiceMonthlyProps
	) => Promise<CreateInvoiceReturnType>;
	createAkontoInvoice: (props: {
		recipientType: "patient" | "guarantor" | "client";
		recipientId: string;
		amount: number;
		bankAccountId: number;
	}) => Promise<{ invoiceNumber: string; filePath: string }>;
	shareInvoice: (invoiceId: number) => Promise<{
		success: boolean;
		invoiceData?: TableInvoiceRow;
		error?: string;
	}>;
	unshareInvoice: (invoiceId: number) => Promise<void>;
	getExtendedJobDocuments: (
		jobDocumentIds: number[]
	) => Promise<ExtendedJobDocument[] | null>;
	fetchAkontoData: () => Promise<void>;
	fetchVerlustData: () => Promise<void>;
	fetchOpenInvoicesData: (
		selectedDate: Date | null,
		options: {
			sortBy?: string;
			sortDirection?: "asc" | "desc";
			pageIndex?: number;
			pageSize?: number;
			globalFilter?: string;
			invoiceDateFrom?: Date | null;
			invoiceDateTo?: Date | null;
			selectedRecipient?: string;
			updateStore: boolean;
		}
	) => Promise<{
		data: OpenInvoicesViewType[];
		totalCount: number;
		error?: string;
	}>;
}

const buildAkontoKey = (
	clientId: string | null,
	guarantorId: string | null,
	patientId: string | null
): string => {
	if (patientId) return `patient:${patientId}`;
	if (guarantorId) return `guarantor:${guarantorId}`;
	if (clientId) return `client:${clientId}`;
	throw new Error("At least one recipient ID must be provided");
};

export const createAccountingStore: StateCreator<AccountingSlice> = (
	set,
	get
) => ({
	accountingData: [],
	invoicePaymentsData: [],
	paymentJournalData: [],
	openInvoicesData: [],
	akontoData: [],
	verlustData: [],
	canceledInvoicesData: [],
	recipientsLookup: {},
	akontoLookup: {},
	refreshSingleAccountingData: async (invoiceId) => {
		// This function is deprecated - accountingData is no longer used
		// Use refreshSingleInvoicePaymentsData instead
		Logger.warn(
			"refreshSingleAccountingData is deprecated, use refreshSingleInvoicePaymentsData"
		);
		await get().refreshSingleInvoicePaymentsData(invoiceId);
	},
	fetchInvoicePaymentsData: async (
		startDate,
		endDate,
		selectedRecipient,
		options
	) => {
		const {
			status = "active",
			sortBy = "invoice_doc_number",
			sortDirection = "desc",
			pageIndex = 0,
			pageSize = 50,
			globalFilter = "",
			columnFilters = [],
			updateStore,
		} = options;
		const organizationId = useCentralStore.getState().organization?.id;
		if (!organizationId) {
			Logger.error("Organization ID not found");
			return { data: [], totalCount: 0 };
		}

		await get().fetchRecipients();

		let clientId: string | undefined;
		let guarantorId: string | undefined;
		if (selectedRecipient && selectedRecipient !== "ALL") {
			const recipient = get().recipientsLookup[selectedRecipient];
			if (!recipient) {
				if (updateStore) set({ invoicePaymentsData: [] });
				return { data: [], totalCount: 0 };
			}
			if (recipient.type === "client") {
				clientId = selectedRecipient;
			} else if (recipient.type === "guarantor") {
				guarantorId = selectedRecipient;
			}
		}

		const paymentStatus = columnFilters.find(
			(f) => f.id === "payment_status"
		)?.value as string | undefined;
		const valueMin = columnFilters.find((f) => f.id === "invoice_value_min")
			?.value as number | undefined;
		const valueMax = columnFilters.find((f) => f.id === "invoice_value_max")
			?.value as number | undefined;
		const taxRate = columnFilters.find((f) => f.id === "effective_tax_rate")
			?.value as number | undefined;
		const sharingStatus = columnFilters.find(
			(f) => f.id === "sharing_status"
		)?.value as string | undefined;

		const { rows, totalCount, error } = await fetchRpcPage(
			pageIndex,
			pageSize,
			(limit, offset) =>
				supabase.rpc("get_invoice_payments", {
					p_org_id: organizationId,
					p_status: status,
					p_date_from: startDate || undefined,
					p_date_to: endDate || undefined,
					p_client_id: clientId,
					p_guarantor_id: guarantorId,
					p_value_min: valueMin,
					p_value_max: valueMax,
					p_tax_rate: taxRate,
					p_payment_status: paymentStatus,
					p_sharing_status: sharingStatus,
					p_search_text: globalFilter || undefined,
					p_sort_column: sortBy,
					p_sort_direction: sortDirection,
					p_limit: limit,
					p_offset: offset,
				})
		);

		if (error) {
			Logger.error(error, {}, "Error fetching invoice payments");
			return { data: [], totalCount: 0, error };
		}

		const resultData = rows as unknown as TableInvoiceRow[];

		if (updateStore) {
			set(
				produce((state) => {
					if (status === "canceled") {
						state.canceledInvoicesData = resultData;
					} else {
						state.invoicePaymentsData = resultData;
					}
				})
			);
		}

		return { data: resultData, totalCount };
	},
	fetchPaymentJournalData: async (
		startDate,
		endDate,
		selectedRecipient,
		options
	) => {
		const {
			sortBy = "valuta_date",
			sortDirection = "desc",
			pageIndex = 0,
			pageSize = 50,
			globalFilter = "",
			updateStore,
		} = options;
		const organizationId = useCentralStore.getState().organization?.id;
		if (!organizationId) {
			Logger.error("Organization ID not found");
			return { data: [], totalCount: 0 };
		}

		await get().fetchRecipients();

		let clientId: string | undefined;
		let guarantorId: string | undefined;
		if (selectedRecipient && selectedRecipient !== "ALL") {
			const recipient = get().recipientsLookup[selectedRecipient];
			if (!recipient) {
				if (updateStore) set({ paymentJournalData: [] });
				return { data: [], totalCount: 0 };
			}
			if (recipient.type === "client") {
				clientId = selectedRecipient;
			} else if (recipient.type === "guarantor") {
				guarantorId = selectedRecipient;
			}
		}

		const { rows, totalCount, error } = await fetchRpcPage(
			pageIndex,
			pageSize,
			(limit, offset) =>
				supabase.rpc("get_payment_journal", {
					p_org_id: organizationId,
					p_date_from: startDate || undefined,
					p_date_to: endDate || undefined,
					p_client_id: clientId,
					p_guarantor_id: guarantorId,
					p_search_text: globalFilter || undefined,
					p_sort_column: sortBy,
					p_sort_direction: sortDirection,
					p_limit: limit,
					p_offset: offset,
				})
		);

		if (error) {
			Logger.error(error, {}, "Error fetching payment journal data");
			return { data: [], totalCount: 0, error };
		}

		const resultData = rows as unknown as PaymentJournalRow[];

		if (updateStore) set({ paymentJournalData: resultData });

		return { data: resultData, totalCount };
	},
	refreshSingleInvoicePaymentsData: async (invoiceId) => {
		useRemindersStore.getState().fetchDueCount();
		// Try to refresh the invoice in all data arrays
		const state = get();

		// Check invoice_with_payments_view first
		const { data: invoiceData, error: invoiceError } = await supabase
			.from("invoice_with_payments_view")
			.select()
			.eq("invoice_id", invoiceId);

		if (invoiceError) {
			Logger.error(
				"Error refreshing invoice payments data:" + invoiceError
			);
			return;
		}

		if (invoiceData && invoiceData.length > 0) {
			// Update in invoicePaymentsData if found there
			if (
				state.invoicePaymentsData.some(
					(inv) => inv.invoice_id === invoiceId
				)
			) {
				set(
					produce((state) => {
						const index = state.invoicePaymentsData.findIndex(
							(inv: TableInvoiceRow) =>
								inv.invoice_id === invoiceId
						);
						if (index !== -1) {
							state.invoicePaymentsData[index] = invoiceData[0];
						}
					})
				);
				return;
			}

			// Update in canceledInvoicesData if found there
			if (
				state.canceledInvoicesData.some(
					(inv) => inv.invoice_id === invoiceId
				)
			) {
				set(
					produce((state) => {
						const index = state.canceledInvoicesData.findIndex(
							(inv: TableInvoiceRow) =>
								inv.invoice_id === invoiceId
						);
						if (index !== -1) {
							state.canceledInvoicesData[index] = invoiceData[0];
						}
					})
				);
				return;
			}
		}

		// Check open invoices view
		const { data: openData, error: openError } = await supabase
			.from("open_invoices_view")
			.select()
			.eq("invoice_id", invoiceId);

		if (!openError && openData && openData.length > 0) {
			// Update in openInvoicesData if found there
			if (
				state.openInvoicesData.some(
					(inv) => inv.invoice_id === invoiceId
				)
			) {
				set(
					produce((state) => {
						const index = state.openInvoicesData.findIndex(
							(inv: TableInvoiceRow) =>
								inv.invoice_id === invoiceId
						);
						if (index !== -1) {
							state.openInvoicesData[index] = openData[0];
						}
					})
				);
				return;
			}
		}

		// If invoice not found in any view, remove it from all arrays
		set(
			produce((state) => {
				state.invoicePaymentsData = state.invoicePaymentsData.filter(
					(inv: TableInvoiceRow) => inv.invoice_id !== invoiceId
				);
				state.canceledInvoicesData = state.canceledInvoicesData.filter(
					(inv: TableInvoiceRow) => inv.invoice_id !== invoiceId
				);
				state.paymentJournalData = state.paymentJournalData.filter(
					(inv: TableInvoiceRow) => inv.invoice_id !== invoiceId
				);
				state.openInvoicesData = state.openInvoicesData.filter(
					(inv: TableInvoiceRow) => inv.invoice_id !== invoiceId
				);
			})
		);
	},
	fetchRecipients: async () => {
		const organizationId = useCentralStore.getState().organization?.id;
		if (!organizationId) return Logger.error("Organization ID not found");

		const { data, error } = await supabase
			.from(SupabaseTableEnum.ACCOUNTING_RECIPIENTS)
			.select("*")
			.eq("organization_id", organizationId);

		if (error) {
			Logger.error(error);
			return;
		}

		if (data) {
			const recipientsLookup = data.reduce(
				(acc: Record<string, RecipientsLookupValue>, curr) => {
					if (!curr.recipient_id) {
						return acc;
					}

					const getDisplayNameData = {
						is_company: curr.recipient_is_company,
						company_name: curr.recipient_company_name,
						first_name: curr.recipient_first_name,
						last_name: curr.recipient_last_name,
					};
					const displayName =
						curr.recipient_type === "c"
							? getClientDisplayName(
									getDisplayNameData as any,
									true
								)
							: getGuarantorDisplayName(
									getDisplayNameData as any
								);

					acc[curr.recipient_id] = {
						type:
							curr.recipient_type === "c"
								? RecipientTypeEnum.CLIENT
								: RecipientTypeEnum.GUARANTOR,
						name: displayName,
					};
					return acc;
				},
				{}
			);

			set({ recipientsLookup: recipientsLookup });
		}
	},
	fetchAkontoView: async () => {
		const organizationId = useCentralStore.getState().organization?.id;
		if (!organizationId) {
			Logger.error("Organization ID not found when fetching akonto view");
			return;
		}

		const { data, error } = await supabase
			.from("akonto_view")
			.select("*")
			.eq("organization_id", organizationId);

		if (error) {
			Logger.error(error, {}, "Error fetching akonto view");
			return;
		}

		// Populate entire lookup
		set(
			produce((state) => {
				state.akontoLookup = {};
				data?.forEach((row) => {
					const key = buildAkontoKey(
						row.recipient_client_id,
						row.recipient_guarantor_id,
						row.recipient_patient_id
					);
					state.akontoLookup[key] = row.outstanding_amount ?? 0;
				});
			})
		);
	},
	getAkonto: async (
		recipientClientId: string | null,
		recipientGuarantorId: string | null,
		recipientPatientId: string | null
	): Promise<number> => {
		await get().fetchAkontoView();
		const key = buildAkontoKey(
			recipientClientId,
			recipientGuarantorId,
			recipientPatientId
		);
		return get().akontoLookup[key] ?? 0;
	},
	clearAkontoLookup: () => set({ akontoLookup: {} }),
	createInvoiceSingle: async ({
		fileNamePrefix,
		invoiceValue,
		bankAccount,
		invoiceType,
		pdfTemplateType,
		client,
		organization,
		jobDocument,
		job,
		akontoAmount,
	}: CreateInvoiceSingleProps): Promise<CreateInvoiceReturnType> => {
		const clientId = client.id;
		if (!clientId) {
			Logger.error("Client ID not found when creating invoice single");
			return { success: false, data: null, error: "Client ID not found" };
		}
		const organizationId = organization.id;
		if (!organizationId) {
			Logger.error(
				"Organization ID not found when creating invoice single"
			);
			return {
				success: false,
				data: null,
				error: "Organization ID not found",
			};
		}

		// Determine recipient based on job payment conditions
		let recipientClientId: string | null = null;
		let recipientGuarantorId: string | null = null;
		let recipientPatientId: string | null = null;

		if (job.is_paid_by_patient) {
			recipientPatientId = job.patient_id;
		} else if (job.guarantor_id) {
			recipientGuarantorId = job.guarantor_id;
		} else {
			recipientClientId = clientId;
		}

		// Use provided akonto amount, default to 0 if not provided
		const akontoToUse = akontoAmount ?? 0;

		const { activatedRegions } = useCentralStore.getState();
		const currency = getCurrencyFromRegion(
			client,
			organization,
			activatedRegions
		);

		const rpcParams = {
			file_name_prefix: fileNamePrefix,
			invoice_value: invoiceValue,
			invoice_bank_account_id: bankAccount.id,
			invoice_type: invoiceType,
			pdf_template_type: pdfTemplateType,
			invoice_currency: currency,
			invoice_client_id: clientId,
			invoice_guarantor_id: null as unknown as string, // TODO: can normal invoices go to guarantors?
			invoice_organization_id: organizationId,
			job_document_id: jobDocument.id,
			job_id: job.id as number,
			p_akonto_amount: akontoToUse,
		};
		const { data, error } = await supabase.rpc(
			SupabaseRpcEnum.CREATE_INVOICE_SINGLE,
			rpcParams
		);
		if (error) {
			return { success: false, data: null, error: error.message };
		}

		const invoiceNumber = data[0].invoice_number;
		const filePath = data[0].file_path;

		if (!invoiceNumber || !filePath) {
			return {
				success: false,
				data: null,
				error: "No invoice number or file path returned",
			};
		}

		return {
			success: true,
			data: { invoiceNumber, filePath, akontoUsed: akontoToUse },
			error: null,
		};
	},
	createInvoiceMonthly: async ({
		invoiceValue,
		bankAccountId,
		clientId,
		guarantorId,
		organizationId,
		jobDocumentIds,
		invoiceDate,
		akontoAmount,
	}: CreateInvoiceMonthlyProps): Promise<CreateInvoiceReturnType> => {
		if (!clientId && !guarantorId) {
			return {
				success: false,
				data: null,
				error: "Either client or guarantor must be set.",
			};
		}

		// Use provided akonto amount, default to 0 if not provided
		const akontoToUse = akontoAmount ?? 0;

		const organization = useCentralStore.getState().organization;
		if (!organization) {
			return {
				success: false,
				data: null,
				error: "Organization not found",
			};
		}

		let client: ClientEntityType | null = null;
		if (clientId) {
			const { clientsLookup } = useCentralStore.getState();
			client = clientsLookup[clientId] || null;
		}

		const { activatedRegions } = useCentralStore.getState();
		const currency = getCurrencyFromRegion(
			client,
			organization,
			activatedRegions
		);

		const formattedInvoiceDate = `${invoiceDate.getFullYear()}-${String(
			invoiceDate.getMonth() + 1
		).padStart(2, "0")}-${String(invoiceDate.getDate()).padStart(2, "0")}`;

		const { data, error } = await supabase.rpc(
			SupabaseRpcEnum.CREATE_INVOICE_MONTHLY,
			{
				file_name_prefix: "Sammelrechnung-",
				invoice_value: invoiceValue,
				invoice_bank_account_id: bankAccountId,
				invoice_type: InvoiceType.MONATSRG,
				pdf_template_type: PdfTemplateType.SAMMELRECHNUNG,
				invoice_currency: currency,
				invoice_client_id: clientId as string,
				invoice_guarantor_id: guarantorId as string,
				invoice_organization_id: organizationId,
				job_document_ids: jobDocumentIds,
				invoice_date: formattedInvoiceDate,
				p_akonto_amount: akontoToUse,
			}
		);
		if (error) {
			Logger.error(error, {}, "Error creating monthly invoice via rpc");
			return { success: false, data: null, error: error.message };
		}

		const invoiceNumber = (data[0] as unknown as { invoice_number: string })
			.invoice_number;
		const filePath = (data[0] as unknown as { file_path: string })
			.file_path;

		if (!invoiceNumber || !filePath) {
			return {
				success: false,
				data: null,
				error: "No invoice number or file path returned",
			};
		}

		return {
			success: true,
			data: { invoiceNumber, filePath, akontoUsed: akontoToUse },
			error: null,
		};
	},
	createAkontoInvoice: async ({
		recipientType,
		recipientId,
		amount,
		bankAccountId,
	}: {
		recipientType: "patient" | "guarantor" | "client";
		recipientId: string;
		amount: number;
		bankAccountId: number;
	}): Promise<{ invoiceNumber: string; filePath: string }> => {
		const organization = useCentralStore.getState().organization;
		if (!organization?.id) {
			throw new Error("Organization not found");
		}

		const { clientsLookup } = useCentralStore.getState();

		let recipientClientId: string | null = null;
		let recipientGuarantorId: string | null = null;
		let recipientPatientId: string | null = null;
		let clientForFile: string | null = null;
		let clientForCurrency: ClientEntityType | null = null;

		if (recipientType === "client") {
			recipientClientId = recipientId;
			clientForFile = recipientId;
			clientForCurrency = clientsLookup[recipientId] || null;
		} else if (recipientType === "guarantor") {
			recipientGuarantorId = recipientId;
			const clientWithGuarantor = Object.values(clientsLookup).find(
				(c) => c.guarantor_id === recipientId
			);
			clientForFile = clientWithGuarantor?.id || null;
			clientForCurrency = clientWithGuarantor || null;
		} else {
			recipientPatientId = recipientId;
			const { patientsLookup } = usePatientStore.getState();
			const patient = patientsLookup[recipientId];
			clientForFile = patient?.client_id || null;
			if (patient?.client_id) {
				clientForCurrency = clientsLookup[patient.client_id] || null;
			}
		}

		const { activatedRegions } = useCentralStore.getState();
		const currency = getCurrencyFromRegion(
			clientForCurrency,
			organization,
			activatedRegions
		);

		const { data: rpcData, error: rpcError } = await supabase.rpc(
			SupabaseRpcEnum.CREATE_AKONTO_INVOICE,
			{
				invoice_value: amount,
				invoice_bank_account_id: bankAccountId,
				invoice_currency: currency,
				invoice_organization_id: organization.id,
				recipient_client_id: recipientClientId ?? undefined,
				recipient_guarantor_id: recipientGuarantorId ?? undefined,
				recipient_patient_id: recipientPatientId ?? undefined,
				client_id_for_file: clientForFile ?? undefined,
			}
		);

		if (rpcError) {
			Logger.error(rpcError, {}, "Error creating akonto invoice via rpc");
			throw new Error(rpcError.message);
		}

		const invoiceNumber = (
			rpcData[0] as unknown as { invoice_number: string }
		).invoice_number;
		const filePath = (rpcData[0] as unknown as { file_path: string })
			.file_path;
		const invoiceId = (rpcData[0] as unknown as { invoice_id: number })
			.invoice_id;

		if (!invoiceNumber || !filePath || !invoiceId) {
			throw new Error(
				"No invoice number, file path, or invoice ID returned"
			);
		}

		showNotification({
			message: `Akontorechnung ${invoiceNumber} erfolgreich erstellt`,
			type: "success",
		});

		return { invoiceNumber, filePath };
	},
	markInvoiceAsPaid: async (invoiceId) => {
		const { error } = await supabase.rpc(SupabaseRpcEnum.SET_INVOICE_PAID, {
			given_invoice_id: invoiceId,
		});

		if (error) {
			Logger.error(error);
			return;
		}

		await get().refreshSingleAccountingData(invoiceId);
		await get().refreshSingleInvoicePaymentsData(invoiceId);
	},
	createPayment: async ({
		invoiceId,
		amount,
		valutaDate,
		paymentType,
		note,
		generatePaymentSlip,
		bookRemainderAsLoss,
		lossComment,
	}: CreatePaymentProps): Promise<CreatePaymentReturnType> => {
		const { data, error } = await supabase.rpc(
			SupabaseRpcEnum.CREATE_PAYMENT,
			{
				p_invoice_id: invoiceId,
				p_amount: amount,
				p_valuta_date: valutaDate,
				p_payment_type: paymentType,
				p_note: note.trim() || undefined,
				p_book_remainder_as_loss: bookRemainderAsLoss,
				p_loss_comment: lossComment.trim() || undefined,
			}
		);

		if (error) {
			Logger.error(error);
			showNotification({
				message: "Fehler beim Erstellen der Zahlung: " + error.message,
				type: "error",
			});
			return { success: false, data: null, error: error.message };
		}

		const result = data as any;

		if (!result.success) {
			showNotification({
				message:
					"Fehler beim Erstellen der Zahlung: " +
					(result.error || "Unbekannter Fehler"),
				type: "error",
			});
			return {
				success: false,
				data: null,
				error: result.error || "Unbekannter Fehler",
			};
		}

		// TODO: Implement payment slip generation if requested
		if (generatePaymentSlip) {
			// This will be implemented later when payment slip functionality is ready
			Logger.info(
				"Payment slip generation for remaining amount requested but not yet implemented"
			);
		}

		// Show success notification with akonto/loss info if applicable
		let message = "Zahlung wurde erfolgreich erfasst";
		if (result.akonto_amount && result.akonto_amount > 0) {
			message += ` (inkl. CHF ${result.akonto_amount.toFixed(
				2
			)} als Akonto für künftige Rechnungen)`;
		}
		if (result.loss_amount && result.loss_amount > 0) {
			message += ` (CHF ${result.loss_amount.toFixed(
				2
			)} als Verlust gebucht)`;
		}

		showNotification({
			message,
			type: "success",
		});

		// Refresh both views to show the new payment
		await get().refreshSingleAccountingData(invoiceId);
		await get().refreshSingleInvoicePaymentsData(invoiceId);

		return {
			success: true,
			data: {
				paymentId: result.payment_id,
				allocationId: result.allocation_id,
				akontoAllocationId: result.akonto_allocation_id,
				lossAllocationId: result.loss_allocation_id,
				invoiceFullyPaid: result.invoice_fully_paid,
				invoicePaymentAmount: result.invoice_payment_amount,
				akontoAmount: result.akonto_amount,
				akontoInvoiceId: result.akonto_invoice_id,
				lossAmount: result.loss_amount,
			},
			error: null,
		};
	},
	cancelInvoice: async (
		invoiceId,
		reason,
		rebookAsAkonto = false,
		cancelReminderFees = false
	) => {
		// Validate reason on client side
		if (!reason || reason.trim().length === 0) {
			showNotification({
				message: "Grund für Stornierung ist erforderlich",
				type: "error",
			});
			return;
		}

		const { data, error } = await supabase.rpc("cancel_invoice", {
			invoice_id_param: invoiceId,
			reason_param: reason.trim(),
			rebook_as_akonto: rebookAsAkonto,
			p_cancel_reminder_fees: cancelReminderFees,
		});
		if (!(data as any)?.success) {
			const errorMessage = (data as any)?.error;
			let message = "Fehler beim Stornieren der Rechnung";

			if (errorMessage === "payments exist") {
				message =
					"Es existieren bereits Zahlungen für diese Rechnung. Sie kann nicht mehr storniert werden.";
			} else if (errorMessage === "allocations_exist") {
				message =
					"Es existieren Zahlungszuordnungen für diese Rechnung. Bitte bestätigen Sie die Umbuchung als Akonto.";
			} else if (errorMessage === "cancellation_reason_required") {
				message = "Grund für Stornierung ist erforderlich";
			} else if (errorMessage === "fee_invoice_not_latest_stage") {
				message =
					"Nur die Mahngebühr der höchsten aktiven Mahnstufe kann storniert werden.";
			}

			Logger.error(error);
			showNotification({
				message: message,
				type: "error",
			});
			return;
		}

		const paymentsRebooked = (data as any)?.payments_rebooked;
		const {
			reminder_fees_canceled: reminderFeesCanceled = 0,
			reminder_voided: reminderVoided = false,
		} =
			(data as unknown as {
				reminder_fees_canceled?: number;
				reminder_voided?: boolean;
			} | null) ?? {};
		const message = [
			paymentsRebooked
				? "Rechnung wurde storniert und Zahlungen wurden als Akonto umgebucht."
				: "Rechnung wurde storniert.",
			reminderFeesCanceled > 0
				? `${reminderFeesCanceled} Mahngebühr(en) storniert.`
				: "",
			reminderVoided ? "Die Mahnstufe wurde zurückgenommen." : "",
		]
			.filter(Boolean)
			.join(" ");

		showNotification({
			message: message,
			type: "success",
		});

		await get().refreshSingleAccountingData(invoiceId);
		await get().refreshSingleInvoicePaymentsData(invoiceId);
	},
	cancelPayment: async (
		allocationId: number,
		invoiceId: number,
		note: string
	) => {
		const { data, error } = await supabase.rpc("cancel_invoice_payments", {
			p_allocation_id: allocationId,
			p_note: note,
		});

		if (error || !(data as any)?.success) {
			const errorMessage =
				(data as any)?.error || error?.message || "Unbekannter Fehler";
			showNotification({
				message: `Fehler beim Stornieren der Zahlung: ${errorMessage}`,
				type: "error",
			});
			Logger.error("Error canceling payment" + error);
			return;
		}

		showNotification({
			message: "Zahlung wurde storniert",
			type: "success",
		});

		await get().refreshSingleInvoicePaymentsData(invoiceId);
	},
	cancelLossAllocation: async (
		allocationId: number,
		invoiceId: number,
		note: string
	) => {
		const { data, error } = await supabase.rpc(
			SupabaseRpcEnum.CANCEL_LOSS_ALLOCATION,
			{
				p_allocation_id: allocationId,
				p_note: note,
			}
		);

		if (error || !(data as any)?.success) {
			const errorMessage =
				(data as any)?.error || error?.message || "Unbekannter Fehler";
			showNotification({
				message: `Fehler beim Stornieren des Verlusts: ${errorMessage}`,
				type: "error",
			});
			Logger.error("Error canceling loss allocation" + error);
			return;
		}

		showNotification({
			message: "Verlust wurde storniert",
			type: "success",
		});

		await get().refreshSingleInvoicePaymentsData(invoiceId);
	},
	rebookPaymentAsAkonto: async (
		allocationId: number,
		invoiceId: number,
		note: string
	) => {
		const { data, error } = await supabase.rpc(
			SupabaseRpcEnum.REBOOK_ALLOCATIONS_AS_AKONTO,
			{
				p_allocation_id: allocationId,
				p_note: note,
			}
		);

		if (error || !(data as any)?.success) {
			const errorMessage =
				(data as any)?.error || error?.message || "Unbekannter Fehler";
			showNotification({
				message: `Fehler beim Umbuchen der Zahlung: ${errorMessage}`,
				type: "error",
			});
			Logger.error("Error rebooking payment as akonto" + error);
			return;
		}

		showNotification({
			message: "Zahlung wurde als Akonto umgebucht",
			type: "success",
		});

		await get().refreshSingleInvoicePaymentsData(invoiceId);
		get().clearAkontoLookup();
	},
	shareInvoice: async (invoiceId) => {
		const state = get();

		// Find the invoice across all data arrays (excluding paymentJournalData which is payment-centric)
		let invoice: TableInvoiceRow | OpenInvoicesViewType | null | undefined =
			null;
		let dataArrayName = null;

		if (
			state.invoicePaymentsData.some(
				(inv) => inv.invoice_id === invoiceId
			)
		) {
			invoice = state.invoicePaymentsData.find(
				(inv) => inv.invoice_id === invoiceId
			);
			dataArrayName = "invoicePaymentsData";
		} else if (
			state.canceledInvoicesData.some(
				(inv) => inv.invoice_id === invoiceId
			)
		) {
			invoice = state.canceledInvoicesData.find(
				(inv) => inv.invoice_id === invoiceId
			);
			dataArrayName = "canceledInvoicesData";
		} else if (
			state.openInvoicesData.some((inv) => inv.invoice_id === invoiceId)
		) {
			invoice = state.openInvoicesData.find(
				(inv) => inv.invoice_id === invoiceId
			);
			dataArrayName = "openInvoicesData";
		}

		if (!invoice || !dataArrayName) {
			showNotification({
				message: "Rechnung konnte nicht identifiziert werden",
				type: "error",
			});
			return {
				success: false,
				error: "Rechnung konnte nicht identifiziert werden",
			};
		}

		// Optimistically update the UI
		set((state) => ({
			...state,
			[dataArrayName]: (
				state[
					dataArrayName as keyof AccountingSlice
				] as TableInvoiceRow[]
			).map((inv: TableInvoiceRow) => {
				if (inv.invoice_id === invoiceId) {
					return {
						...inv,
						shared_at: new Date().toISOString(),
						shared_until: null,
					};
				}
				return inv;
			}),
		}));

		let invoiceData;
		let invoiceError;

		const isAkonto = invoice.invoice_type === 2;

		if (isSrg(invoice) || isAkonto) {
			if (!invoice.file_id) {
				showNotification({
					message:
						"Rechnung konnte nicht geteilt werden, da keine Datei vorhanden ist",
					type: "error",
				});
				return {
					success: false,
					error: "Rechnung konnte nicht geteilt werden, da keine Datei vorhanden ist",
				};
			}
			const { data, error } = await supabase.rpc(
				SupabaseRpcEnum.SHARE_INDEPENDENT_INVOICE,
				{
					p_invoice_id: invoiceId,
					p_message_content: {
						type: "shared_file",
						file_id: invoice.file_id,
						text: "",
					},
				}
			);
			invoiceData = data;
			invoiceError = error;
		} else {
			if (!invoice.file_id) {
				showNotification({
					message:
						"Rechnung konnte nicht geteilt werden, da keine Datei vorhanden ist",
					type: "error",
				});
				return {
					success: false,
					error: "Rechnung konnte nicht geteilt werden, da keine Datei vorhanden ist",
				};
			}
			const { data, error } = await supabase.rpc(
				SupabaseRpcEnum.SHARE_FILE,
				{
					p_share_file_id: invoice.file_id,
					p_message_content: {
						type: "shared_file",
						file_id: invoice.file_id,
						text: "",
					},
				}
			);
			invoiceData = data;
			invoiceError = error;
		}

		if (invoiceError) {
			// Revert the optimistic update
			set((state) => ({
				...state,
				[dataArrayName]: (
					state[
						dataArrayName as keyof AccountingSlice
					] as TableInvoiceRow[]
				).map((inv: TableInvoiceRow) => {
					if (inv.invoice_id === invoiceId) {
						return {
							...inv,
							shared_at: invoice.shared_at, // Restore original value
							shared_until: invoice.shared_until, // Restore original value
						};
					}
					return inv;
				}),
			}));

			showNotification({
				message: "Rechnung konnte nicht geteilt werden.",
				type: "error",
			});
			return {
				success: false,
				error: "Rechnung konnte nicht geteilt werden.",
			};
		}

		if ((invoiceData as any)?.success) {
			showNotification({
				message: "Rechnung wurde geteilt",
				type: "success",
			});
		} else if (!(invoiceData as any)?.success) {
			showNotification({
				message: (invoiceData as any)?.message,
				type: "error",
			});
			return { success: false, error: (invoiceData as any)?.message };
		} else {
			showNotification({
				message: "Rechnung konnte nicht geteilt werden.",
				type: "error",
			});
			return {
				success: false,
				error: "Rechnung konnte nicht geteilt werden.",
			};
		}

		// Refresh the relevant data arrays
		await get().refreshSingleInvoicePaymentsData(invoiceId);

		// Return the invoice data for the email dialog
		return { success: true, invoiceData: invoice as TableInvoiceRow };
	},
	unshareInvoice: async (invoiceId) => {
		const state = get();

		// Find the invoice across all data arrays (excluding paymentJournalData which is payment-centric)
		let invoice = null;
		let dataArrayName = null;

		if (
			state.invoicePaymentsData.some(
				(inv) => inv.invoice_id === invoiceId
			)
		) {
			invoice = state.invoicePaymentsData.find(
				(inv) => inv.invoice_id === invoiceId
			);
			dataArrayName = "invoicePaymentsData";
		} else if (
			state.canceledInvoicesData.some(
				(inv) => inv.invoice_id === invoiceId
			)
		) {
			invoice = state.canceledInvoicesData.find(
				(inv) => inv.invoice_id === invoiceId
			);
			dataArrayName = "canceledInvoicesData";
		} else if (
			state.openInvoicesData.some((inv) => inv.invoice_id === invoiceId)
		) {
			invoice = state.openInvoicesData.find(
				(inv) => inv.invoice_id === invoiceId
			);
			dataArrayName = "openInvoicesData";
		}

		if (!invoice || !dataArrayName) {
			showNotification({
				message: "Rechnung konnte nicht identifiziert werden",
				type: "error",
			});
			return;
		}

		// Optimistically update the UI
		set((state) => ({
			...state,
			[dataArrayName]: (
				state[
					dataArrayName as keyof AccountingSlice
				] as TableInvoiceRow[]
			).map((inv: TableInvoiceRow) => {
				if (inv.invoice_id === invoiceId) {
					return {
						...inv,
						shared_until: new Date().toISOString(),
					};
				}
				return inv;
			}),
		}));

		let invoiceData;
		let invoiceError;
		const isAkonto = invoice.invoice_type === 2;
		if (isSrg(invoice as any) || isAkonto) {
			if (!invoice.file_id) {
				showNotification({
					message:
						"Rechnung konnte nicht geteilt werden, da keine Datei vorhanden ist",
					type: "error",
				});
				return;
			}
			const { data, error } = await supabase.rpc(
				SupabaseRpcEnum.UNSHARE_INDEPENDENT_INVOICE,
				{
					p_invoice_id: invoiceId,
				}
			);
			invoiceData = data;
			invoiceError = error;
		} else {
			if (!invoice.file_id) {
				showNotification({
					message:
						"Rechnung konnte nicht geteilt werden, da keine Datei vorhanden ist",
					type: "error",
				});
				return;
			}
			const { data, error } = await supabase.rpc(
				SupabaseRpcEnum.UNSHARE_FILE,
				{
					unshare_file_id: invoice.file_id,
				}
			);
			invoiceData = data;
			invoiceError = error;
		}

		if (invoiceError) {
			// Revert the optimistic update
			set((state) => ({
				...state,
				[dataArrayName]: (
					state[
						dataArrayName as keyof AccountingSlice
					] as TableInvoiceRow[]
				).map((inv: TableInvoiceRow) => {
					if (inv.invoice_id === invoiceId) {
						return {
							...inv,
							shared_until: invoice.shared_until, // Restore original value
						};
					}
					return inv;
				}),
			}));

			showNotification({
				message: "Fehler beim Aufheben der Rechnungsfreigabe",
				type: "error",
			});
			return;
		}

		if ((invoiceData as any)?.success) {
			showNotification({
				message: "Rechnung wird nicht mehr geteilt",
				type: "success",
			});
		} else if (!(invoiceData as any)?.success) {
			showNotification({
				message: (invoiceData as any)?.message,
				type: "error",
			});
		} else {
			showNotification({
				message: "Fehler beim Aufheben der Rechnungsfreigabe",
				type: "error",
			});
		}

		// Refresh the relevant data arrays
		await get().refreshSingleInvoicePaymentsData(invoiceId);
	},
	getExtendedJobDocuments: async (jobDocumentIds: number[]) => {
		const { data: extendedJobDocuments } = await supabase
			.from("job_documents_extended_view")
			.select("*")
			.in("job_document_id", jobDocumentIds);

		if (!extendedJobDocuments) {
			showNotification({
				message: "Keine erweiterten Auftragsdokumente gefunden",
				type: "error",
			});
			Logger.error("No extended job documents found");
			return null;
		}

		return extendedJobDocuments as ExtendedJobDocument[]; //TODO fix type
	},
	fetchAkontoData: async () => {
		const organizationId = useCentralStore.getState().organization?.id;

		if (!organizationId) {
			Logger.error("No organization found");
			return;
		}

		const { data, error } = await supabase
			.from("akonto_view")
			.select("*")
			.eq("organization_id", organizationId)
			.order("akonto_invoice_id", { ascending: false });

		if (error) {
			showNotification({
				message: "Fehler beim Laden der Akonto-Daten",
				type: "error",
			});
			Logger.error("Error fetching akonto data" + error);
			return;
		}

		set({ akontoData: data as AkontoViewType[] });
	},
	fetchVerlustData: async () => {
		const organizationId = useCentralStore.getState().organization?.id;
		if (!organizationId) {
			Logger.error("No organization found");
			return;
		}
		const { data, error } = await supabase
			.from("verlust_view")
			.select("*")
			.eq("organization_id", organizationId)
			.order("total_loss_amount", { ascending: true });
		if (error) {
			showNotification({
				message: "Fehler beim Laden der Verlust-Daten",
				type: "error",
			});
			Logger.error("Error fetching verlust data" + error);
			return;
		}
		set({ verlustData: data as VerlustViewType[] });
	},
	fetchOpenInvoicesData: async (selectedDate, options) => {
		const organizationId = useCentralStore.getState().organization?.id;
		if (!organizationId) {
			Logger.error("No organization found");
			return { data: [] as OpenInvoicesViewType[], totalCount: 0 };
		}

		const {
			sortBy = "invoice_doc_number",
			sortDirection = "desc",
			pageIndex = 0,
			pageSize = 50,
			globalFilter = "",
			invoiceDateFrom = null,
			invoiceDateTo = null,
			selectedRecipient = "",
			updateStore,
		} = options;

		// Ensure recipients are fetched for filtering
		await get().fetchRecipients();
		const recipient = selectedRecipient
			? get().recipientsLookup[selectedRecipient]
			: null;

		if (selectedDate) {
			// Validate date syntax before calling RPC
			if (isNaN(selectedDate.getTime())) {
				Logger.error(
					"Invalid date syntax provided for historical open invoices" +
						selectedDate
				);
				return { data: [] as OpenInvoicesViewType[], totalCount: 0 };
			}

			// Use RPC for historical data
			try {
				// Create a new date with the same date but set to end of day
				const endOfDay = new Date(selectedDate);
				endOfDay.setHours(23, 59, 59, 999);

				let resultData: OpenInvoiceAtDateRow[] = [];
				let expectedRows: number | null = null;
				do {
					const { data, error, count } = await supabase
						.rpc(
							"get_open_invoices_at_date",
							{
								p_organization_id: organizationId,
								target_date: `${formatLocalDate(endOfDay)} 23:59:59`,
							},
							{ count: "exact" }
						)
						.order("invoice_id")
						.range(
							resultData.length,
							resultData.length + SUPABASE_PAGE_SIZE - 1
						);

					if (error) {
						Logger.error(
							error,
							{},
							"Error fetching historical open invoices"
						);
						return {
							data: [] as OpenInvoicesViewType[],
							totalCount: 0,
							error: error.message,
						};
					}
					if (expectedRows !== null && count !== expectedRows) {
						return {
							data: [] as OpenInvoicesViewType[],
							totalCount: 0,
							error: "Rows changed while loading",
						};
					}
					expectedRows = count ?? 0;
					if (!data?.length) break;
					resultData = resultData.concat(data);
				} while (resultData.length < expectedRows);

				// Exclude invoices dated after the selected historical date
				resultData = resultData.filter((invoice: any) => {
					if (!invoice.invoice_date) return true;
					const invoiceDate = new Date(invoice.invoice_date);
					return invoiceDate <= endOfDay;
				});

				// Apply invoice date range filter in memory
				if (invoiceDateFrom || invoiceDateTo) {
					resultData = resultData.filter((invoice: any) => {
						if (!invoice.invoice_date) return false;
						const invoiceDate = new Date(invoice.invoice_date);
						if (invoiceDateFrom && invoiceDate < invoiceDateFrom)
							return false;
						if (invoiceDateTo) {
							const endOfDay = new Date(invoiceDateTo);
							endOfDay.setHours(23, 59, 59, 999);
							if (invoiceDate > endOfDay) return false;
						}
						return true;
					});
				}

				// Apply recipient filter in memory
				if (selectedRecipient && recipient) {
					resultData = resultData.filter((invoice: any) => {
						if (recipient.type === "client") {
							return (
								invoice.recipient_client_id ===
								selectedRecipient
							);
						} else if (recipient.type === "guarantor") {
							return (
								invoice.recipient_guarantor_id ===
								selectedRecipient
							);
						}
						return true;
					});
				}

				// Apply global filter in memory
				if (globalFilter) {
					resultData = resultData.filter((invoice: any) => {
						const searchFields = [
							invoice.invoice_doc_number,
							invoice.recipient_first_name,
							invoice.recipient_last_name,
							invoice.recipient_company_name,
							invoice.client_first_name,
							invoice.client_last_name,
							invoice.client_company_name,
						];
						return searchFields.some(
							(field) =>
								field &&
								field
									.toLowerCase()
									.includes(globalFilter.toLowerCase())
						);
					});
				}

				// Apply sorting in memory using the new sorting utility
				resultData = sortTableData(
					resultData,
					sortBy,
					sortDirection as "asc" | "desc"
				);

				// Apply pagination in memory
				const totalCount = resultData.length;
				const startIndex = pageIndex * pageSize;
				const endIndex = startIndex + pageSize;
				resultData = resultData.slice(startIndex, endIndex);

				if (updateStore) {
					set({
						openInvoicesData:
							resultData as unknown as OpenInvoicesViewType[],
					});
				}
				return {
					data: resultData as unknown as OpenInvoicesViewType[],
					totalCount,
				};
			} catch (error) {
				Logger.error(
					error,
					{},
					"Error fetching historical open invoices"
				);
				return {
					data: [] as OpenInvoicesViewType[],
					totalCount: 0,
					error: String(error),
				};
			}
		} else {
			try {
				let clientId: string | undefined;
				let guarantorId: string | undefined;
				if (selectedRecipient && recipient) {
					if (recipient.type === "client") {
						clientId = selectedRecipient;
					} else if (recipient.type === "guarantor") {
						guarantorId = selectedRecipient;
					}
				}

				const { rows, totalCount, error } = await fetchRpcPage(
					pageIndex,
					pageSize,
					(limit, offset) =>
						supabase.rpc("get_open_invoices", {
							p_org_id: organizationId,
							p_date_from: invoiceDateFrom
								? formatLocalDate(invoiceDateFrom)
								: undefined,
							p_date_to: invoiceDateTo
								? formatLocalDate(invoiceDateTo)
								: undefined,
							p_client_id: clientId,
							p_guarantor_id: guarantorId,
							p_search_text: globalFilter || undefined,
							p_sort_column: sortBy,
							p_sort_direction: sortDirection,
							p_limit: limit,
							p_offset: offset,
						})
				);

				if (error) {
					Logger.error(error, {}, "Error fetching open invoices");
					return {
						data: [] as OpenInvoicesViewType[],
						totalCount: 0,
						error,
					};
				}

				const resultData = rows as unknown as OpenInvoicesViewType[];

				if (updateStore) set({ openInvoicesData: resultData });
				return { data: resultData, totalCount };
			} catch (error) {
				Logger.error(error, {}, "Error fetching current open invoices");
				return {
					data: [] as OpenInvoicesViewType[],
					totalCount: 0,
					error: String(error),
				};
			}
		}
	},
});
